<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-subtitle
            ><v-icon left small class="mr-2">mdi-card-account-details</v-icon
            >Allgemeine Pflichtangaben</v-card-subtitle
          >
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.trim="personnelRecordBaseLocal.firstName"
                  label="Vorname"
                  hide-details="auto"
                  :rules="[rules.required]"
                  outlined
                ></v-text-field
              ></v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.trim="personnelRecordBaseLocal.lastName"
                  label="Nachname"
                  hide-details="auto"
                  :rules="[rules.required]"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card outlined>
          <v-card-subtitle
            ><v-icon left small class="mr-2">mdi-card-account-details</v-icon
            >Profileigenschaften</v-card-subtitle
          >
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.trim="personnelRecordBaseLocal.personnelNumber"
                  label="Personalnummer"
                  hide-details="auto"
                  outlined
                  prepend-inner-icon="mdi-identifier"
                ></v-text-field
              ></v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="personnelRecordBaseLocal.status"
                  :items="statusOptions"
                  label="Status"
                  :rules="[rules.required]"
                  hide-details="auto"
                  outlined
                >
                  <template v-slot:selection="{ item }">
                    <personnel-record-status-chip
                      :status="item.text"
                    ></personnel-record-status-chip>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-card outlined>
                  <v-card-subtitle class="pb-2">
                    <v-icon left small class="mr-2">mdi-home-group</v-icon>
                    Einheit/Abteilung wählen
                  </v-card-subtitle>
                  <v-card-text class="pt-0">
                    <v-treeview
                      :items="divisionTree"
                      :active.sync="activeDivision"
                      :open.sync="openNodes"
                      activatable
                      selection-type="independent"
                      dense
                      hoverable
                      open-on-click
                      item-key="id"
                      item-text="title"
                      item-children="children"
                      return-object
                      :open-all="true"
                      item-disabled="disabled"
                      @update:active="handleDivisionSelect"
                    >
                      <template v-slot:prepend="{ item }">
                        <v-icon v-if="item.isOrganization"> mdi-domain </v-icon>
                        <v-icon v-else> mdi-home-group </v-icon>
                      </template>
                      <template v-slot:label="{ item }">
                        <div class="py-1">
                          <div
                            :class="
                              item.isOrganization
                                ? 'font-weight-bold'
                                : 'font-weight-medium'
                            "
                          >
                            {{ item.title }}
                          </div>
                          <div
                            v-if="item.shortTitle"
                            class="text-caption grey--text"
                          >
                            {{ item.shortTitle }}
                          </div>
                        </div>
                      </template>
                    </v-treeview>
                    <div
                      v-if="showDivisionError"
                      class="v-messages error--text mt-2"
                      role="alert"
                    >
                      <div class="v-messages__message">
                        Bitte wählen Sie eine Einheit/Abteilung aus.
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PersonnelRecordStatusChip from "@/components/member-management/personnel-records/PersonnelRecordStatusChip.vue";
import { MEMBER_MANAGEMENT } from "@/store/modules.js";
import { GET_DIVISIONS } from "@/store/action-types.js";

export default {
  name: "personnel-record-creation-editor",
  props: {
    personnelRecordBase: {},
  },
  components: {
    PersonnelRecordStatusChip,
  },
  data() {
    return {
      activeDivision: [],
      openNodes: ["org-root"], // Start with organization root open
      showDivisionError: false,
      rules: {
        required: (value) => !!value || "Feld ist erforderlich.",
      },
      statusOptions: [
        { text: "Aktiv", value: "Aktiv" },
        { text: "Im Übertritt", value: "Im Übertritt" },
        { text: "Beurlaubt", value: "Beurlaubt" },
        { text: "Ausgetreten", value: "Ausgetreten" },
        { text: "Entlassen", value: "Entlassen" },
        { text: "Verstorben", value: "Verstorben" },
      ],
    };
  },
  model: {
    prop: "personnelRecordBase",
    event: "personnelRecordBaseChange",
  },
  computed: {
    personnelRecordBaseLocal: {
      get: function () {
        return this.personnelRecordBase;
      },
      set: function (value) {
        this.$emit("personnelRecordBaseChange", value);
      },
    },
    divisions() {
      return this.$store.state.memberManagement.divisions || [];
    },
    organizationName() {
      return (
        this.$store.state.organization.activeOrganization?.displayName ||
        "Organisation"
      );
    },
    defaultDivisionId() {
      return (
        this.$store.state.organization.activeOrganization?.config
          ?.memberManagement?.defaultDivisionId || null
      );
    },
    divisionTree() {
      // Create tree structure with organization as root and divisions as children
      const divisionChildren = this.divisions
        .filter((d) => d.isActive)
        .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0))
        .map((division) => ({
          id: division.meta.id,
          title: division.title,
          shortTitle: division.shortTitle,
          sortKey: division.sortKey,
          isOrganization: false,
          disabled: false,
          children: [],
        }));

      return [
        {
          id: "org-root",
          title: this.organizationName,
          isOrganization: true,
          disabled: true, // Organization root is not selectable
          children: divisionChildren,
        },
      ];
    },
    isDivisionSelected() {
      return !!this.personnelRecordBaseLocal.division;
    },
  },
  watch: {
    personnelRecordBase: {
      immediate: true,
      handler(newVal) {
        // Initialize active division if division is already set
        if (newVal?.division && this.divisions.length > 0) {
          this.initializeActiveDivision();
        }
      },
    },
    divisions: {
      immediate: true,
      handler() {
        // If no division is set yet, use the default division
        if (!this.personnelRecordBase?.division && this.defaultDivisionId) {
          this.setDefaultDivision();
        }
        // Initialize active division when divisions are loaded
        if (this.personnelRecordBase?.division) {
          this.initializeActiveDivision();
        }
      },
    },
  },
  created() {
    this.loadDivisions();
  },
  methods: {
    loadDivisions() {
      this.$store.dispatch(`${MEMBER_MANAGEMENT}${GET_DIVISIONS}`, {
        organizationId: this.$route.params.organizationId,
      });
    },
    setDefaultDivision() {
      // Set the default division if available and divisions are loaded
      if (this.defaultDivisionId && this.divisions.length > 0) {
        const defaultDivision = this.divisions.find(
          (d) => d.meta.id === this.defaultDivisionId
        );
        if (defaultDivision) {
          this.personnelRecordBaseLocal.division = this.defaultDivisionId;
          this.initializeActiveDivision();
        }
      }
    },
    handleDivisionSelect(items) {
      // When a division is selected, update the model
      if (items.length > 0) {
        const selectedDivision = items[0];
        if (!selectedDivision.isOrganization) {
          this.personnelRecordBaseLocal.division = selectedDivision.id;
          this.showDivisionError = false;
        }
      } else {
        // Clear selection if no items are active
        this.personnelRecordBaseLocal.division = null;
        this.showDivisionError = true;
      }
    },
    initializeActiveDivision() {
      // Set the active division in the treeview based on the model value
      const divisionId = this.personnelRecordBase?.division;
      if (divisionId && this.divisions.length > 0) {
        const division = this.divisions.find((d) => d.meta.id === divisionId);
        if (division) {
          // Set the active division in the treeview
          this.activeDivision = [
            {
              id: division.meta.id,
              title: division.title,
              shortTitle: division.shortTitle,
              isOrganization: false,
            },
          ];
        }
      }
    },
    validateDivision() {
      // Public method that can be called from parent to validate
      this.showDivisionError = !this.isDivisionSelected;
      return this.isDivisionSelected;
    },
  },
};
</script>
